import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import moment from 'moment';
import * as yup from 'yup';

const gtZero = (value) => value !== '' && value !== null && value !== undefined && value > 0;

const getQuantidadeMeses = (dataInicio, dataFim) => {
  let meses = 0;

  // Verifica se o mês da vigência inicial é cheio
  const isMesCheioVigenciaInicial = dataInicio.date() === 1;

  // Verifica se o mês da vigência final é cheio
  const isMesCheioVigenciaFinal = dataFim.date() === dataFim.daysInMonth();

  // Calcula quantidade de meses cheios
  const anos = dataFim.year() - dataInicio.year();
  meses = anos * 12 + (dataFim.month() - dataInicio.month() + 1);

  if (!isMesCheioVigenciaInicial) {
    meses -= 1;
  }
  if (!isMesCheioVigenciaFinal) {
    meses -= 1;
  }

  return meses;
};

const getQuantidadeDias = (dataInicio, dataFim, meses) => {
  let dias = 0;

  // Verifica se o mês da vigência inicial é cheio
  const isMesCheioVigenciaInicial = dataInicio.date() === 1;

  // Verifica se o mês da vigência final é cheio
  const isMesCheioVigenciaFinal = dataFim.date() === dataFim.daysInMonth();

  // Calcula quantidade de dias
  if (dataInicio.month() === dataFim.month() && meses <= 0) {
    dias = dataFim.date() - dataInicio.date() + 1;
  } else {
    if (!isMesCheioVigenciaInicial) {
      dias = dataInicio.daysInMonth() - dataInicio.date() + 1;
    }
    if (!isMesCheioVigenciaFinal) {
      dias += dataFim.date();
    }
  }

  return dias;
};

const mailFormat = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

const adesaoSchema = yup.object().shape({
  servico: yup
    .object()
    .nullable()
    .test('servico', mensagemCampoObrigatorio('o serviço'), function(servico) {
      return !this.parent.cobrarAdesao || !!servico?.id;
    }),
  categoria: yup
    .object()
    .nullable()
    .test('categoria', mensagemCampoObrigatorio('a categoria'), function(categoria) {
      return !this.parent.cobrarAdesao || !!categoria?.id;
    }),
  valor: yup
    .number()
    .nullable()
    .test('valor', mensagemCampoObrigatorio('o valor'), function(valor) {
      return !this.parent.cobrarAdesao || gtZero(valor);
    }),
  formaCobranca: yup
    .string()
    .nullable()
    .test('formaCobranca', mensagemCampoObrigatorio('a forma de recebimento'), function(formaCobranca) {
      return !this.parent.cobrarAdesao || !!formaCobranca;
    }),
  dataVencimento: yup
    .date()
    .nullable()
    .test('dataVencimento', mensagemCampoObrigatorio('a data do vencimento'), function(dataVencimento) {
      return !this.parent.cobrarAdesao || !!dataVencimento;
    }),
});

const servicoSchema = yup
  .array()
  .min(1)
  .of(
    yup.object().shape({
      servico: yup
        .object()
        .nullable()
        .required(mensagemCampoObrigatorio('o serviço'))
        .test('servico', mensagemCampoObrigatorio('o serviço'), function(servico) {
          return !!servico && !!servico?.id;
        }),
      valor: yup
        .number()
        .nullable()
        .required(mensagemCampoObrigatorio('o valor')),
    })
  );

const categoriaSchema = yup
  .array()
  .min(1)
  .of(
    yup.object().shape({
      classe: yup
        .object()
        .nullable()
        .required(mensagemCampoObrigatorio('a categoria'))
        .test('classe', mensagemCampoObrigatorio('a categoria'), function(classe) {
          return !!classe && !!classe?.id;
        }),
      valorBruto: yup
        .number()
        .nullable()
        .required(mensagemCampoObrigatorio('o valor bruto')),
      descricao: yup.string().nullable(),
    })
  );

const reajusteAutomaticoSchema = yup.object().shape({
  periodicidadeMeses: yup
    .number()
    .nullable()
    .required(mensagemCampoObrigatorio('a periodicidade.'))
    .test('Periodicidade', mensagemCampoObrigatorio('a periodicidade.'), function(periodicidade) {
      return !this.parent.aplicarReajusteAutomatico || (!!periodicidade && periodicidade > 0);
    }),
  reajustarPor: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o reajuste contratos por.'))
    .test('AjustePor', mensagemCampoObrigatorio('o reajuste contratos por.'), function(tipoReajuste) {
      return !this.parent.aplicarReajusteAutomatico || !!tipoReajuste;
    }),
  valorReajuste: yup
    .number()
    .nullable()
    .when('reajustarPor', (reajustarPor, schema) => {
      switch (reajustarPor) {
        case 'VALOR':
          return schema
            .required(mensagemCampoObrigatorio('o valor'))
            .test('valorReajuste', mensagemCampoObrigatorio('o valor'), function(valorReajuste) {
              if (this.parent.reajustarPor === 'VALOR') {
                return !this.parent.aplicarReajusteAutomatico || (!!valorReajuste && valorReajuste >= 0.02);
              } else {
                return true;
              }
            });
        case 'PERCENTUAL':
        default:
          return schema
            .required(mensagemCampoObrigatorio('o percentual'))
            .test('valorReajuste', mensagemCampoObrigatorio('o percentual'), function(valorReajuste) {
              if (this.parent.reajustarPor === 'PERCENTUAL') {
                return !this.parent.aplicarReajusteAutomatico || !!valorReajuste;
              } else {
                return true;
              }
            });
      }
    }),
  indiceMonetario: yup
    .string()
    .nullable()
    .test('indiceMonetario', mensagemCampoObrigatorio('o índice monetário'), function(indice) {
      if (
        this.parent.aplicarReajusteAutomatico &&
        !!this.parent.reajustarPor &&
        this.parent.reajustarPor === 'INDICE'
      ) {
        if (indice) {
          return indice.length > 0;
        }
        return false;
      }
      return true;
    }),
  mesBaseReajuste: yup
    .date()
    .nullable()
    .test('mesBaseReajuste', mensagemCampoObrigatorio('o mês base para reajuste.'), function(mesBaseReajuste) {
      return !this.parent.aplicarReajusteAutomatico || !!mesBaseReajuste;
    }),
});

const ContratoSchema = yup.object().shape({
  cliente: yup
    .object()
    .nullable()
    .test('cliente', mensagemCampoObrigatorio('o cliente'), function(cliente) {
      return !!cliente?.id;
    }),
  dataVigenciaInicial: yup
    .date()
    .nullable()
    .required(mensagemCampoObrigatorio('a vigência inicial')),
  vigenciaIndeterminada: yup.boolean(),
  dataVigenciaFinal: yup
    .date()
    .nullable()
    .when('vigenciaIndeterminada', {
      is: false,
      then: yup.date().required(mensagemCampoObrigatorio('a vigência final')),
    })
    .test('periodoDataVigencia', 'Informe um período de até 200 meses', function(dataVigenciaFinal) {
      const dataVigenciaInicial = this.parent.dataVigenciaInicial;

      const meses = getQuantidadeMeses(moment(dataVigenciaInicial), moment(dataVigenciaFinal));
      const dias = getQuantidadeDias(moment(dataVigenciaInicial), moment(dataVigenciaFinal), meses);

      return !this.parent.vigenciaIndeterminada && ((meses === 200 && dias > 0) || meses > 200) ? false : true;
    })
    .test('dataVigenciaSuperior', 'A vigência final deve ser superior à vigência inicial', function(dataVigenciaFinal) {
      return !this.parent.dataVigenciaInicial || !dataVigenciaFinal
        ? true
        : dataVigenciaFinal > this.parent.dataVigenciaInicial;
    }),
  servicos: servicoSchema,
  categorias: categoriaSchema,
  _exigeImovel: yup.boolean(),
  imovel: yup
    .object()
    .nullable()
    .when('_exigeImovel', {
      is: true,
      then: yup
        .object()
        .nullable()
        .test('imovel', mensagemCampoObrigatorio('o imóvel'), function(imovel) {
          return !!imovel?.id;
        }),
    }),
  vencimentoCobranca: yup
    .number()
    .nullable()
    .required(mensagemCampoObrigatorio('o vencimento da cobrança')),
  formaCobranca: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a forma de recebimento')),
  emailsCliente: yup.array().when('envioAutomaticoCobranca', {
    is: true,
    then: yup.array().of(
      yup.object().shape({
        email: yup
          .string()
          .nullable()
          .required(mensagemCampoObrigatorio('o e-mail'))
          .test('email', 'E-mail inválido', function(email) {
            return email?.match(mailFormat);
          }),
      })
    ),
  }),
  formaRecebimentoPrimeiraCobranca: yup
    .string()
    .nullable()
    .test('formaRecebimentoPrimeiraCobranca', mensagemCampoObrigatorio('a forma de recebimento'), function(
      formaRecebimentoPrimeiraCobranca
    ) {
      return !this.parent.recebimentoDiferentePrimeiraCobranca || !!formaRecebimentoPrimeiraCobranca;
    }),
  dataVencimentoPrimeiraCobranca: yup
    .date()
    .nullable()
    .test(
      'dataVencimentoPrimeiraCobranca',
      'O vencimento da 1ª cobrança não pode ser menor que a vigência inicial',
      function(dataVencimentoPrimeiraCobranca) {
        if (this.parent.dataVigenciaInicial && dataVencimentoPrimeiraCobranca) {
          return dataVencimentoPrimeiraCobranca >= this.parent.dataVigenciaInicial;
        }
        return true;
      }
    )
    .test('dataVencimentoPrimeiraCobranca', mensagemCampoObrigatorio('a data do vencimento'), function(
      dataVencimentoPrimeiraCobranca
    ) {
      return !this.parent.recebimentoDiferentePrimeiraCobranca || !!dataVencimentoPrimeiraCobranca;
    }),
  reajusteAutomatico: reajusteAutomaticoSchema,
  adesao: adesaoSchema,
  diferencaCategorias: yup
    .number()
    .test('diferencaCategorias', 'A diferença deve ser R$0,00', function(diferencaCategorias) {
      return !diferencaCategorias;
    }),
});

export default ContratoSchema;
